import { type ReactNode } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";

type Direction = "up" | "down" | "left" | "right" | "none";

interface RevealProps {
  children: ReactNode;
  /** délai (ms) — pour les cascades */
  delay?: number;
  direction?: Direction;
  /** distance de glissement (px) */
  distance?: number;
  /** échelle de départ */
  scale?: number;
  /** flou de départ (px) */
  blur?: number;
  /** durée (ms) */
  duration?: number;
  style?: React.CSSProperties;
}

const dirOffset = (d: Direction, dist: number) => {
  switch (d) {
    case "up":    return { y: dist };
    case "down":  return { y: -dist };
    case "left":  return { x: -dist };
    case "right": return { x: dist };
    default:      return {};
  }
};

// Courbe « easeOutExpo » — départ vif, arrivée très douce (rendu cinéma).
const EASE = [0.16, 1, 0.3, 1] as const;

// Révèle son contenu (fondu + glissement + léger zoom + flou) quand il entre
// dans la vue. Joué une seule fois. Désactivé si « prefers-reduced-motion ».
export default function Reveal({
  children,
  delay = 0,
  direction = "up",
  distance = 44,
  scale = 0.96,
  blur = 8,
  duration = 950,
  style,
}: RevealProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div style={style}>{children}</div>;
  }

  const variants: Variants = {
    hidden: {
      opacity: 0,
      scale,
      filter: `blur(${blur}px)`,
      ...dirOffset(direction, distance),
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: { duration: duration / 1000, delay: delay / 1000, ease: EASE },
    },
  };

  return (
    <motion.div
      style={style}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
    >
      {children}
    </motion.div>
  );
}
